import { dateFormats } from '@shared/lang/date-formats.js'
import { createI18n } from 'vue-i18n'
import { PRIORITIES } from '@/core/constants/installation-priorities.constant'

export const priority = PRIORITIES.I18N

class TranslationLoader {
  constructor(locale) {
    this.locale = locale

    this.messages = {
      [locale]: {
      },
    }
  }

  loadCommonTranslations() {
    const commonTranslations = import.meta.glob('@/shared/lang/*.json', {
      eager: true,
    })
    this.mergeTranslations(commonTranslations)
    return this
  }

  loadModuleTranslations() {
    const translations = import.meta.glob('@/modules/**/i18n/*.json', {
      eager: true,
    })
    this.mergeTranslations(translations)
    return this
  }

  deepMerge(target, source) {
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        if (!target[key])
          target[key] = {}
        this.deepMerge(target[key], source[key]) // Recursively merge
      }
      else {
        target[key] = source[key] // Overwrite non-object values
      }
    }
  }

  mergeTranslations(translations) {
    Object.entries(translations).forEach(([filePath, module]) => {
      if (filePath.endsWith(`/${this.locale}.json`)) {
        const newTranslations = module.default || {}

        if (!this.messages[this.locale]) {
          this.messages[this.locale] = {}
        }
        this.deepMerge(this.messages[this.locale], newTranslations) // Apply deep merge
        // this.messages[this.locale] = {
        //   ...this.messages[this.locale],
        //   ...translations,
        // }
      }
    })
  }

  getMessages() {
    return this.messages
  }
}

export class I18nService {
  static FALLBACK_LOCALE = 'en'
  static STORAGE_KEY = 'locale'
  static SUPPORTED_LOCALES = ['en', 'pt-br', 'es-la', 'zh-cn', 'el', 'de', 'ja']

  static normalizeLocale(locale) {
    if (!locale)
      return locale
    const lower = String(locale).toLowerCase()
    // Canonicalize greek region variants (el, el-GR, etc) to app key "el".
    if (lower.startsWith('el'))
      return 'el'
    return lower
  }

  static getBrowserLocale() {
    const browserLang = navigator.language.toLowerCase()

    if (
      browserLang === 'zh-cn'
      || browserLang.startsWith('zh-cn')
      || browserLang === 'zh-sg'
      || browserLang.startsWith('zh-hans')
    ) {
      return 'zh-cn'
    }

    return (
      this.SUPPORTED_LOCALES.find(locale =>
        browserLang.startsWith(locale.toLowerCase()),
      ) || this.FALLBACK_LOCALE
    )
  }

  static getInitialLocale() {
    const stored = localStorage.getItem(this.STORAGE_KEY)
    return this.normalizeLocale(stored) || this.getBrowserLocale()
  }

  static loadTranslationsForLocale(locale) {
    return new TranslationLoader(this.normalizeLocale(locale))
      .loadCommonTranslations()
      .loadModuleTranslations()
      .getMessages()
  }

  static createI18nInstance() {
    const initialLocale = this.getInitialLocale()
    const fallbackMessages = this.loadTranslationsForLocale(
      this.FALLBACK_LOCALE,
    )
    const initialMessages = { ...fallbackMessages }

    if (initialLocale !== this.FALLBACK_LOCALE) {
      const localeMessages = this.loadTranslationsForLocale(initialLocale)
      initialMessages[initialLocale] = {
        ...initialMessages[initialLocale],
        ...localeMessages[initialLocale],
      }
    }

    return createI18n({
      locale: initialLocale,
      fallbackLocale: this.FALLBACK_LOCALE,
      messages: initialMessages,
      datetimeFormats: dateFormats,
      legacy: false,
    })
  }
}

export const i18n = I18nService.createI18nInstance()

export function install({ app }) {
  app.use(i18n)
}
